import { toast } from '@heroui/react';
import * as React from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { format, parse, isValid, parseISO } from 'date-fns';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useLocation } from 'react-router-dom';

import {
  Button,
  Fieldset,
  Input as HeroUiInput,
  Label,
  ListBox,
  Select,
  Spinner,
  Switch,
  TextArea as HeroUiTextArea,
  TextField,
} from '@heroui/react';
import { apiClient } from '@hs/shared';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import { LocationPickerDialog } from '@/components/LocationPickerDialog';
import { DateTimePickerField } from '@/components/custom/datetime-picker';
import { PrizesStepContent } from './PrizesStepContent';
import { LotteryRulesStepContent } from './LotteryRulesStepContent';
import { IconMapPin } from '@tabler/icons-react';
import {
  CalendarDateTime,
  ZonedDateTime,
  fromDate,
  getLocalTimeZone,
} from '@internationalized/date';
import type { DateValue } from '@internationalized/date';

/** HeroUI `Input`/`TextArea`（RAC）与部分原生属性的类型定义偏紧，本页用薄封装承接表单用法 */
function Input(props: Record<string, unknown>) {
  return <HeroUiInput {...props} />;
}

function TextArea(props: Record<string, unknown>) {
  return <HeroUiTextArea {...props} />;
}

/** 与原先 Upload 列表项兼容的最小结构（本页本地上传 UI 使用） */
type EditorUploadFile = {
  uid: string;
  name?: string;
  url?: string;
  thumbUrl?: string;
  status?: string;
  percent?: number;
  originFileObj?: File;
  response?: { id?: string; url?: string };
};

/** `datetime-local` 需 `step={1}` 才出现秒；`value` 需带秒（yyyy-MM-ddTHH:mm:ss）与 step 一致 */
const DATETIME_LOCAL_MINUTES = "yyyy-MM-dd'T'HH:mm";
const DATETIME_LOCAL_SECONDS = "yyyy-MM-dd'T'HH:mm:ss";

function formatDateTimeLocal(date: Date | string | null | undefined): string {
  if (!date) return '';
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (Number.isNaN(d.getTime())) return '';
    return format(d, DATETIME_LOCAL_SECONDS);
  } catch {
    return '';
  }
}

function parseDateTimeLocal(value: string): string {
  if (!value?.trim()) return '';
  const trimmed = value.trim();

  const viaIso = parseISO(trimmed);
  if (isValid(viaIso)) {
    return viaIso.toISOString();
  }

  let d = parse(trimmed, DATETIME_LOCAL_SECONDS, new Date());
  if (!isValid(d)) {
    d = parse(trimmed, DATETIME_LOCAL_MINUTES, new Date());
  }
  if (!isValid(d)) return '';
  return d.toISOString();
}

/** 统计周期展示与本地字符串解析（与配置 JSON 一致） */
const STAT_DATETIME_FORMAT = 'yyyy-MM-dd HH:mm:ss';

/** `DateTimePickerField`（本地时区）与表单 ISO 字符串互转，精度到分 */
function isoStringToDateValue(iso: string | null | undefined): DateValue | undefined {
  if (!iso?.trim()) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return fromDate(d, getLocalTimeZone());
}

function dateValueToIsoString(v: DateValue | null): string {
  if (v == null) return '';
  if (v instanceof ZonedDateTime) return v.toDate().toISOString();
  if (v instanceof CalendarDateTime) return v.toDate(getLocalTimeZone()).toISOString();
  return v.toDate(getLocalTimeZone()).toISOString();
}

export type StatisticalPeriodItem = {
  label: string;
  start_time: string;
  end_time: string;
};

function parseConfigPeriods(config: unknown): StatisticalPeriodItem[] {
  if (!config || typeof config !== 'object') return [];
  const raw = (config as Record<string, unknown>).statisticalPeriods;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (item): item is Record<string, unknown> =>
        item != null && typeof item === 'object' && typeof (item as { label?: unknown }).label === 'string'
    )
    .map((item) => ({
      label: String(item.label),
      start_time: String(item.start_time ?? ''),
      end_time: String(item.end_time ?? ''),
    }))
    .filter((p) => p.start_time && p.end_time);
}

function formatPeriodDisplay(iso: string): string {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return isValid(d) ? format(d, STAT_DATETIME_FORMAT) : '-';
  } catch {
    return '-';
  }
}

function localStatStringToISO(value: string): string {
  if (!value) return '';
  try {
    const d = parse(value, STAT_DATETIME_FORMAT, new Date());
    return isValid(d) ? d.toISOString() : '';
  } catch {
    return '';
  }
}

/** 统计周期添加：兼容旧 UI 的本地字符串或 datetime-local 解析得到的 ISO */
function normalizeStatTimeToIso(raw: string): string {
  if (!raw) return '';
  if (raw.includes('T') || raw.endsWith('Z')) {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString();
  }
  return localStatStringToISO(raw);
}

function getImageSize(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = reject;
    img.src = url;
  });
}

const posterItemSchema = z.object({
  url: z.string().max(500),
  size: z
    .object({
      width: z.number().int().positive('宽度必须为正整数'),
      height: z.number().int().positive('高度必须为正整数'),
    })
    .optional()
    .nullable(),
  name: z
    .string()
    .max(200, '名称不能超过200个字符')
    .optional()
    .nullable()
    .transform((val) => (val === '' || !val ? null : val)),
  description: z
    .string()
    .max(1000, '说明不能超过1000个字符')
    .optional()
    .nullable()
    .transform((val) => (val === '' || !val ? null : val)),
});

const statisticalPeriodSchema = z.object({
  label: z.string(),
  start_time: z.string(),
  end_time: z.string(),
});

const activitySchema = z.object({
  activityTypePath: z.array(z.string()).min(2, '请选择活动类型'),
  title: z.string().min(1, '活动标题不能为空').max(200, '活动标题不能超过200个字符'),
  coverImage: z
    .string()
    .max(500, '封面图URL不能超过500个字符')
    .optional()
    .nullable()
    .transform((val) => (val === '' || !val ? null : val)),
  description: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === '' || !val ? null : val)),
  startTime: z.string().min(1, '活动开始时间不能为空'),
  endTime: z.string().min(1, '活动结束时间不能为空'),
  signupEndTime: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === '' || !val ? null : val)),
  mode: z.enum(['offline', 'online'] as [string, ...string[]]),
  location: z.record(z.string(), z.unknown()).nullable().optional(),
  onlineLink: z
    .string()
    .max(500, '在线活动链接不能超过500个字符')
    .optional()
    .nullable()
    .transform((val) => (val === '' || !val ? null : val)),
  quota: z.string().optional().nullable(),
  allowCancelSignup: z.boolean().optional(),
  visibility: z.enum(['SELF', 'ORG', 'ORG_AND_CHILD', 'SPACE', 'ASSIGNEE'] as [string, ...string[]]),
  isTemplate: z.boolean().optional(),
  posters: z.array(posterItemSchema),
  statisticalPeriods: z.array(statisticalPeriodSchema),
});

type ActivityFormInput = z.input<typeof activitySchema>;
type ActivityFormOutput = z.output<typeof activitySchema>;
type VisibilityValue = 'SELF' | 'ORG' | 'ORG_AND_CHILD' | 'SPACE' | 'ASSIGNEE';

type ActivityLocationPayload = {
  source?: string;
  provider?: string;
  name?: string;
  address?: string;
  location?: {
    lat?: number;
    lng?: number;
    coordSystem?: string;
  } | null;
  [key: string]: unknown;
};

function normalizeLocationPayload(raw: unknown): ActivityLocationPayload | null {
  if (raw && typeof raw === 'object') return raw as ActivityLocationPayload;
  return null;
}

function getLocationDisplayText(raw: unknown): string {
  if (raw != null && typeof raw === 'object') {
    const payload = raw as ActivityLocationPayload;
    return String(payload.address || payload.name || '').trim();
  }
  return '';
}

const EMPTY_POSTER_ROW = { url: '', size: null as { width: number; height: number } | null, name: null as string | null, description: null as string | null };

function normalizePostersFromConfig(cfg: unknown): Array<z.infer<typeof posterItemSchema>> {
  if (!cfg || typeof cfg !== 'object' || !('posters' in cfg)) {
    return [{ ...EMPTY_POSTER_ROW }];
  }
  const posters = (cfg as { posters?: unknown }).posters;
  if (!Array.isArray(posters) || posters.length === 0) {
    return [{ ...EMPTY_POSTER_ROW }];
  }
  return posters.map((poster: unknown) => {
    const p = poster as Record<string, unknown>;
    let size: { width: number; height: number } | null = null;
    if (p.size && typeof p.size === 'object' && p.size !== null && 'width' in p.size && 'height' in p.size) {
      const w = Number((p.size as { width: unknown }).width);
      const h = Number((p.size as { height: unknown }).height);
      if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
        size = { width: Math.floor(w), height: Math.floor(h) };
      }
    }
    return {
      url: typeof p.url === 'string' ? p.url : '',
      size,
      name: typeof p.name === 'string' ? p.name : null,
      description: typeof p.description === 'string' ? p.description : null,
    };
  });
}

function buildPosterUploadLists(posters: Array<z.infer<typeof posterItemSchema>>): Map<number, EditorUploadFile[]> {
  const m = new Map<number, EditorUploadFile[]>();
  posters.forEach((poster, index) => {
    if (poster.url) {
      m.set(index, [
        {
          uid: `poster-${index}-${Date.now()}`,
          name: poster.name || `海报${index + 1}`,
          url: poster.url,
          thumbUrl: poster.url,
          status: 'done',
        },
      ]);
    }
  });
  return m;
}

export type ActivityFormProps = {
  /** 编辑中的活动 id；`null`/`undefined` 表示新建 */
  activityId?: string | null;
  onClose: () => void;
  /** 新建首次保存成功后由父层写入查询参数（如 `?editor=<id>`），不重挂载子树以保留步骤状态 */
  onPersistedActivityId?: (id: string) => void;
};

export type ActivityFormContextModel = {
  onClose: () => void;
  isEdit: boolean;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  handleStepChange: (next: number) => void;
  stepDefs: readonly { step: number; title: string; description: string }[];
  isSubmitting: boolean;
  submitIntentRef: React.MutableRefObject<'draft' | 'published'>;
  canEnterStep3: boolean;
};

const ActivityFormContext = React.createContext<ActivityFormContextModel | null>(null);

export function useActivityFormContext(): ActivityFormContextModel {
  const v = React.useContext(ActivityFormContext);
  if (!v) throw new Error('useActivityFormContext 须在 ActivityFormProvider 内使用');
  return v;
}

export function ActivityFormProvider({
  children,
  activityId: activityIdProp,
  onClose,
  onPersistedActivityId,
}: ActivityFormProps & {
  children: (parts: { FormBody: React.FC }) => React.ReactNode;
}) {
  const location = useLocation();
  const [createdActivityId, setCreatedActivityId] = React.useState<string | null>(null);
  const effectiveActivityId = (activityIdProp ?? undefined) || createdActivityId || null;
  const isEdit = Boolean(effectiveActivityId);
  const [step, setStep] = React.useState(1);
  const utils = trpc.useUtils();
  const user = useAppSelector((state) => state.auth.user);
  const userId = user?.id;

  const { data: activityData, isLoading: activityLoading } = trpc.activities.get.useQuery(
    { id: effectiveActivityId! },
    { enabled: !!effectiveActivityId }
  );

  const { data: allPrizes = [], refetch: refetchPrizes } = trpc.activityPrizes.listAll.useQuery(
    { activityId: effectiveActivityId! },
    { enabled: !!effectiveActivityId }
  );

  const internalTypeValues = React.useMemo(
    () => new Set(['meeting', 'training', 'commendation', 'teambuilding', 'other']),
    []
  );
  const externalTypeValues = React.useMemo(
    () =>
      new Set([
        'business',
        'social',
        'entertainment',
        'travel',
        'sports',
        'learning',
        'art',
        'charity',
        'family',
        'wellness',
        'competition',
      ]),
    []
  );
  const activityTypeOptions = React.useMemo(
    () => ({
      internal: [
        { value: 'meeting', label: '会议（Meeting）' },
        { value: 'training', label: '培训（Training）' },
        { value: 'commendation', label: '表彰（Commendation）' },
        { value: 'teambuilding', label: '团建（Teambuilding）' },
        { value: 'other', label: '其他（Other）' },
      ],
      external: [
        { value: 'business', label: '商务（Business）' },
        { value: 'social', label: '社交（Social）' },
        { value: 'entertainment', label: '娱乐（Entertainment）' },
        { value: 'travel', label: '旅行（Travel）' },
        { value: 'sports', label: '运动（Sports）' },
        { value: 'learning', label: '学习（Learning）' },
        { value: 'art', label: '艺术（Art）' },
        { value: 'charity', label: '公益（Charity）' },
        { value: 'family', label: '家庭（Family）' },
        { value: 'wellness', label: '健康（Wellness）' },
        { value: 'competition', label: '赛事（Competition）' },
      ],
    }),
    []
  );
  const [coverImageMediaId, setCoverImageMediaId] = React.useState<string | null>(null);
  const [uploadFileList, setUploadFileList] = React.useState<EditorUploadFile[]>([]);
  const [posterMediaIds, setPosterMediaIds] = React.useState<Map<number, string>>(() => new Map());
  const [posterUploadLists, setPosterUploadLists] = React.useState<Map<number, EditorUploadFile[]>>(() => new Map());
  const [amapLocationDialogOpen, setAmapLocationDialogOpen] = React.useState(false);
  const [locationInputText, setLocationInputText] = React.useState('');
  const [statAddLabel, setStatAddLabel] = React.useState('');
  const [statAddStartTime, setStatAddStartTime] = React.useState<string | null>(null);
  const [statAddEndTime, setStatAddEndTime] = React.useState<string | null>(null);
  /** 必须用 ref：点击「立即发布」时 setState 尚未提交，handleSubmit 已同步执行，会读到旧的 draft */
  const submitIntentRef = React.useRef<'draft' | 'published'>('draft');
  const modeLabelMap: Record<string, string> = {
    offline: '线下',
    online: '线上',
  };
  const visibilityLabelMap: Record<VisibilityValue, string> = {
    SELF: '本人',
    ORG: '本组织',
    ORG_AND_CHILD: '本组织及子组织',
    SPACE: '全部',
    ASSIGNEE: '指定人员',
  };

  const deleteMediaMutation = trpc.assets.delete.useMutation({
    onError: () => toast.danger('删除图片失败'),
  });

  const form = useForm<ActivityFormInput, unknown, ActivityFormOutput>({
    resolver: zodResolver(activitySchema),
    defaultValues: {
      activityTypePath: [],
      title: '',
      coverImage: null,
      description: null,
      startTime: '',
      endTime: '',
      signupEndTime: null,
      mode: 'offline',
      location: null,
      onlineLink: null,
      quota: null,
      allowCancelSignup: false,
      visibility: 'SPACE',
      isTemplate: false,
      posters: [{ ...EMPTY_POSTER_ROW }],
      statisticalPeriods: [],
    },
  });

  const {
    fields: posterFields,
    append: appendPoster,
    remove: removePosterAt,
  } = useFieldArray({
    control: form.control,
    name: 'posters',
  });

  const { append: appendPeriod, remove: removePeriodAt } = useFieldArray({
    control: form.control,
    name: 'statisticalPeriods',
  });

  const createActivityMutation = trpc.activities.create.useMutation();
  const updateActivityMutation = trpc.activities.update.useMutation();

  const watchedPosters = form.watch('posters');
  const hasPosterImages = watchedPosters.some((p) => p.url && p.url.trim() !== '');

  const handlePosterUploadSuccess = React.useCallback(
    async (index: number, file: EditorUploadFile, response: { id?: string; url?: string }) => {
      if (!response.url) return;
      const fileName = file.name || file.originFileObj?.name || '';
      const currentPosters = form.getValues('posters');
      const newPosters = [...currentPosters];
      if (index >= 0 && index < newPosters.length) {
        newPosters[index] = {
          ...newPosters[index],
          url: response.url,
          name: newPosters[index].name || fileName || null,
        };
      }
      form.setValue('posters', newPosters);

      if (response.id) {
        setPosterMediaIds((prev) => {
          const m = new Map(prev);
          m.set(index, response.id!);
          return m;
        });
      }

      try {
        const size = await getImageSize(response.url);
        const updatedPosters = form.getValues('posters');
        if (index >= 0 && index < updatedPosters.length) {
          updatedPosters[index] = {
            ...updatedPosters[index],
            size: { width: size.width, height: size.height },
          };
          form.setValue('posters', updatedPosters);
        }
      } catch (error) {
        console.error('获取图片尺寸失败:', error);
      }
    },
    [form]
  );

  const handleAddPosterRow = React.useCallback(() => {
    appendPoster({ ...EMPTY_POSTER_ROW });
  }, [appendPoster]);

  const handleRemoveLastPoster = React.useCallback(() => {
    const currentPosters = form.getValues('posters');
    if (currentPosters.length <= 1) {
      form.setValue('posters', [{ ...EMPTY_POSTER_ROW }]);
      const mediaId = posterMediaIds.get(0);
      if (mediaId) {
        deleteMediaMutation.mutate({ id: mediaId });
        setPosterMediaIds((prev) => {
          const m = new Map(prev);
          m.delete(0);
          return m;
        });
      }
      setPosterUploadLists((prev) => {
        const m = new Map(prev);
        m.delete(0);
        return m;
      });
      return;
    }
    const lastIndex = currentPosters.length - 1;
    const mediaId = posterMediaIds.get(lastIndex);
    if (mediaId) {
      deleteMediaMutation.mutate({ id: mediaId });
    }
    removePosterAt(lastIndex);
    setPosterMediaIds((prev) => {
      const remapped = new Map<number, string>();
      prev.forEach((value, key) => {
        if (key < lastIndex) remapped.set(key, value);
      });
      return remapped;
    });
    setPosterUploadLists((prev) => {
      const remapped = new Map<number, EditorUploadFile[]>();
      prev.forEach((value, key) => {
        if (key < lastIndex) remapped.set(key, value);
      });
      return remapped;
    });
  }, [form, posterMediaIds, deleteMediaMutation, removePosterAt]);

  const handleAddStatisticalPeriod = React.useCallback(() => {
    const label = statAddLabel.trim();
    if (!label) {
      toast.danger('请输入周期名称');
      return;
    }
    if (!statAddStartTime || !statAddEndTime) {
      toast.danger('请选择开始时间和结束时间');
      return;
    }
    const start_time = normalizeStatTimeToIso(statAddStartTime);
    const end_time = normalizeStatTimeToIso(statAddEndTime);
    if (!start_time || !end_time) {
      toast.danger('时间格式无效');
      return;
    }
    if (new Date(start_time) > new Date(end_time)) {
      toast.danger('开始时间不能晚于结束时间');
      return;
    }
    appendPeriod({ label, start_time, end_time });
    setStatAddLabel('');
    setStatAddStartTime(null);
    setStatAddEndTime(null);
  }, [statAddLabel, statAddStartTime, statAddEndTime, appendPeriod]);

  React.useLayoutEffect(() => {
    if (!effectiveActivityId) {
      form.reset({
        activityTypePath: [],
        title: '',
        coverImage: null,
        description: null,
        startTime: '',
        endTime: '',
        signupEndTime: null,
        mode: 'offline',
        location: null,
        onlineLink: null,
        quota: null,
        allowCancelSignup: false,
        visibility: 'SPACE',
        isTemplate: false,
        posters: [{ ...EMPTY_POSTER_ROW }],
        statisticalPeriods: [],
      });
      setUploadFileList([]);
      setCoverImageMediaId(null);
      setPosterMediaIds(new Map());
      setPosterUploadLists(new Map());
      setLocationInputText('');
      setStatAddLabel('');
      setStatAddStartTime(null);
      setStatAddEndTime(null);
      return;
    }

    if (activityData) {
      const cfg = activityData.config as Record<string, unknown> | null | undefined;
      const postersNormalized = normalizePostersFromConfig(cfg);
      const periodsNormalized = parseConfigPeriods(cfg);
      const normalizedLocation = normalizeLocationPayload(activityData.location);

      form.reset({
        activityTypePath:
          activityData.type === 'internal'
            ? ['internal', activityData.internalType || 'other']
            : ['external', activityData.externalType || 'business'],
        title: activityData.title || '',
        coverImage: activityData.coverImage || null,
        description: activityData.description || null,
        startTime: formatDateTimeLocal(activityData.startTime),
        endTime: formatDateTimeLocal(activityData.endTime),
        signupEndTime: activityData.signupEndTime
          ? formatDateTimeLocal(activityData.signupEndTime)
          : null,
        mode: activityData.mode || 'offline',
        location: normalizedLocation,
        onlineLink: activityData.onlineLink || null,
        quota: activityData.quota ? String(activityData.quota) : null,
        allowCancelSignup: activityData.allowCancelSignup ?? false,
        visibility: (activityData.visibility ?? 'SPACE') as VisibilityValue,
        isTemplate: activityData.isTemplate ?? false,
        posters: postersNormalized,
        statisticalPeriods: periodsNormalized,
      });
      setUploadFileList(
        activityData.coverImage
          ? [{ uid: 'url-1', name: '封面图', url: activityData.coverImage, thumbUrl: activityData.coverImage, status: 'done' }]
          : []
      );
      setCoverImageMediaId(null);
      setPosterMediaIds(new Map());
      setPosterUploadLists(buildPosterUploadLists(postersNormalized));
      setLocationInputText(getLocationDisplayText(activityData.location));
      setStatAddLabel('');
      setStatAddStartTime(null);
      setStatAddEndTime(null);
    }
  }, [effectiveActivityId, activityData, form]);

  const onSubmit = async (values: ActivityFormOutput) => {
    if (!userId) {
      toast.danger('无法获取用户信息，请重新登录');
      return;
    }
    try {
      const parsedQuota = values.quota && values.quota !== '' ? Number.parseInt(values.quota, 10) : null;
      const [pathType, pathSubType] = Array.isArray(values.activityTypePath) ? values.activityTypePath : [];
      const normalizedType =
        pathType === 'internal' || pathType === 'external'
          ? pathType
          : internalTypeValues.has(pathSubType || '')
            ? 'internal'
            : 'external';
      const isInternalActivity = normalizedType === 'internal';
      const internalType =
        isInternalActivity && internalTypeValues.has(pathSubType || '')
          ? pathSubType
          : isInternalActivity
            ? 'other'
            : null;
      const externalType =
        !isInternalActivity && externalTypeValues.has(pathSubType || '')
          ? pathSubType
          : !isInternalActivity
            ? 'business'
            : null;

      const validPosters = values.posters.filter((poster) => poster.url && poster.url.trim() !== '');
      const baseConfig =
        effectiveActivityId && activityData?.config && typeof activityData.config === 'object'
          ? { ...(activityData.config as Record<string, unknown>) }
          : {};

      const submitData = {
        type: normalizedType,
        internalType,
        externalType,
        title: values.title,
        coverImage: values.coverImage,
        description: values.description,
        startTime: parseDateTimeLocal(values.startTime),
        endTime: parseDateTimeLocal(values.endTime),
        signupEndTime: values.signupEndTime ? parseDateTimeLocal(values.signupEndTime) : null,
        status: submitIntentRef.current,
        mode: values.mode,
        location: values.location,
        onlineLink: values.onlineLink,
        quota: parsedQuota == null || Number.isNaN(parsedQuota) ? null : parsedQuota,
        currentParticipants: activityData?.currentParticipants ?? 0,
        allowCancelSignup: values.allowCancelSignup ?? false,
        visibility: values.visibility as VisibilityValue,
        isTemplate: values.isTemplate ?? false,
        initiatorId: userId,
        orgId: null,
        spaceId: null,
        config: {
          ...baseConfig,
          posters: validPosters,
          statisticalPeriods: values.statisticalPeriods,
        },
      };

      if (effectiveActivityId) {
        const editId = effectiveActivityId;
        if (!editId) {
          toast.danger('缺少活动ID，无法保存');
          return;
        }
        await updateActivityMutation.mutateAsync({ id: editId, data: submitData });
        toast.success('活动更新成功');
        await Promise.all([
          utils.activities.list.invalidate(),
          utils.activities.listMy.invalidate(),
          utils.activities.get.invalidate({ id: editId }),
        ]);
      } else {
        const created = await createActivityMutation.mutateAsync(submitData);
        toast.success('活动创建成功');
        await Promise.all([
          utils.activities.list.invalidate(),
          utils.activities.listMy.invalidate(),
        ]);
        if (created?.id) {
          const nextId = String(created.id);
          setCreatedActivityId(nextId);
          onPersistedActivityId?.(nextId);
          setStep(2);
        }
      }
    } catch (error) {
      console.error('提交活动失败:', error);
    }
  };

  const isSubmitting = createActivityMutation.isPending || updateActivityMutation.isPending;

  const coverFileInputRef = React.useRef<HTMLInputElement>(null);
  const [coverUploadBusy, setCoverUploadBusy] = React.useState(false);

  const removeCoverImage = React.useCallback(async () => {
    if (coverImageMediaId) {
      try {
        await deleteMediaMutation.mutateAsync({ id: coverImageMediaId });
      } catch {
        return;
      }
    }
    setCoverImageMediaId(null);
    form.setValue('coverImage', null);
    setUploadFileList([]);
  }, [coverImageMediaId, deleteMediaMutation, form]);

  const onCoverFileSelected = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || isSubmitting) return;
      setCoverUploadBusy(true);
      const uid = `cover-${Date.now()}`;
      setUploadFileList([{ uid, name: file.name, status: 'uploading', percent: 0 }]);
      try {
        const res = await apiClient.uploadFile({
          file,
          onProgress: (p) => {
            setUploadFileList([{ uid, name: file.name, status: 'uploading', percent: p.percent }]);
          },
        });
        if (res.url) form.setValue('coverImage', res.url);
        if (res.id) setCoverImageMediaId(res.id);
        setUploadFileList([
          {
            uid,
            name: file.name,
            status: 'done',
            url: res.url,
            thumbUrl: res.url,
            response: res,
          },
        ]);
      } catch {
        toast.danger('上传失败');
        setUploadFileList([]);
      } finally {
        setCoverUploadBusy(false);
      }
    },
    [form, isSubmitting]
  );

  const handlePosterFileInput = React.useCallback(
    async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || isSubmitting) return;
      const uid = `poster-${index}-${Date.now()}`;
      const uploadingItem: EditorUploadFile = {
        uid,
        name: file.name,
        status: 'uploading',
        percent: 0,
      };
      setPosterUploadLists((prev) => {
        const m = new Map(prev);
        m.set(index, [uploadingItem]);
        return m;
      });
      try {
        const res = await apiClient.uploadFile({
          file,
          onProgress: (p) => {
            setPosterUploadLists((prev) => {
              const m = new Map(prev);
              m.set(index, [{ ...uploadingItem, percent: p.percent }]);
              return m;
            });
          },
        });
        const doneItem: EditorUploadFile = {
          uid,
          name: file.name,
          status: 'done',
          url: res.url,
          thumbUrl: res.url,
          response: res,
        };
        setPosterUploadLists((prev) => {
          const m = new Map(prev);
          m.set(index, [doneItem]);
          return m;
        });
        if (res.url) await handlePosterUploadSuccess(index, doneItem, res);
      } catch {
        toast.danger('上传失败');
        setPosterUploadLists((prev) => {
          const m = new Map(prev);
          m.delete(index);
          return m;
        });
      }
    },
    [handlePosterUploadSuccess, isSubmitting]
  );

  const removePosterImageAt = React.useCallback(
    async (index: number) => {
      const mediaId = posterMediaIds.get(index);
      if (mediaId) {
        try {
          await deleteMediaMutation.mutateAsync({ id: mediaId });
        } catch {
          return;
        }
      }
      const currentPosters = form.getValues('posters');
      const newPosters = [...currentPosters];
      if (index < newPosters.length) {
        newPosters[index] = {
          url: '',
          size: null,
          name: null,
          description: null,
        };
        form.setValue('posters', newPosters);
      }
      setPosterMediaIds((prev) => {
        const m = new Map(prev);
        m.delete(index);
        return m;
      });
      setPosterUploadLists((prev) => {
        const m = new Map(prev);
        m.delete(index);
        return m;
      });
    },
    [deleteMediaMutation, form, posterMediaIds]
  );

  const modeValue = form.watch('mode');
  const allowCancelSignupValue = form.watch('allowCancelSignup');
  const isTemplateValue = form.watch('isTemplate');
  const statisticalPeriodsValues = form.watch('statisticalPeriods');
  const errors = form.formState.errors;
  const canEnterStep2 = Boolean(effectiveActivityId);
  const canEnterStep3 = Boolean(effectiveActivityId) && allPrizes.length > 0;
  const stepDefs = [
    { step: 1, title: '活动设置', description: '基础信息与活动配置' },
    { step: 2, title: '奖品设置', description: '维护活动奖品' },
    { step: 3, title: '抽奖规则', description: '配置抽奖规则' },
  ] as const;

  const handleStepChange = React.useCallback(
    (next: number) => {
      if (next === 2 && !canEnterStep2) {
        toast.danger('请先保存活动设置，生成活动后再进入奖品设置');
        return;
      }
      if (next === 3) {
        if (!canEnterStep2) {
          toast.danger('请先保存活动设置');
          return;
        }
        if (!canEnterStep3) {
          toast.danger('请先在第2步至少添加一个奖品');
          return;
        }
      }
      setStep(next);
    },
    [canEnterStep2, canEnterStep3]
  );

  React.useEffect(() => {
    const q = new URLSearchParams(location.search).get('step');
    if (!q) return;
    const n = Number.parseInt(q, 10);
    if (Number.isNaN(n)) return;
    handleStepChange(n);
  }, [location.search, handleStepChange]);

  const ActivityFormBody: React.FC = () => {
    if (isEdit && activityLoading) {
      return (
        <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center" data-slot="activity-form-loading">
          <Spinner className="h-8 w-8" />
        </div>
      );
    }

    return (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col p-4" data-slot="activity-form-scroll-wrap">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div
            className="grid min-h-0 min-w-0 w-full flex-1 grid-rows-[minmax(0,1fr)] gap-y-0"
            data-slot="activity-editor-grid-main"
          >
            <div className="relative min-h-0 min-w-0 overflow-hidden">
              <div className="absolute inset-0 flex min-h-0 min-w-0 flex-col overflow-y-auto overflow-x-hidden overscroll-contain">
                <div className="box-border min-h-0 pb-8">
          {step === 1 && (
          <form id="activity-editor-form" onSubmit={form.handleSubmit(onSubmit)} className="w-full max-w-2xl space-y-4">
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                活动类型 <span className="text-danger">*</span>
              </Label>
              <Controller
                control={form.control}
                name="activityTypePath"
                render={({ field }) => {
                  const path = (field.value as string[]) || [];
                  const parent = path[0] as 'internal' | 'external' | undefined;
                  const child = path[1];
                  const childOptions = parent ? activityTypeOptions[parent] : [];
                  return (
                    <div className="flex w-full flex-col gap-2 sm:flex-row">
                      <Select
                        className="w-full min-w-0 sm:flex-1"
                        isDisabled={isSubmitting}
                        value={parent}
                        onChange={(key) => {
                          const k = key != null ? String(key) : '';
                          if (k === 'internal' || k === 'external') {
                            const first =
                              activityTypeOptions[k][0]?.value ??
                              (k === 'internal' ? 'other' : 'business');
                            field.onChange([k, first]);
                            return;
                          }
                          field.onChange([]);
                        }}
                      >
                        <Select.Trigger className="w-full">
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                          <ListBox>
                            <ListBox.Item id="internal" textValue="内部活动">
                              内部活动
                              <ListBox.ItemIndicator />
                            </ListBox.Item>
                            <ListBox.Item id="external" textValue="市场活动">
                              市场活动
                              <ListBox.ItemIndicator />
                            </ListBox.Item>
                          </ListBox>
                        </Select.Popover>
                      </Select>
                      <Select
                        className="w-full min-w-0 sm:flex-1"
                        isDisabled={isSubmitting || !parent}
                        value={child}
                        onChange={(key) => {
                          if (parent && key != null) field.onChange([parent, String(key)]);
                        }}
                      >
                        <Select.Trigger className="w-full">
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                          <ListBox>
                            {childOptions.map((opt) => (
                              <ListBox.Item key={opt.value} id={opt.value} textValue={opt.label}>
                                {opt.label}
                                <ListBox.ItemIndicator />
                              </ListBox.Item>
                            ))}
                          </ListBox>
                        </Select.Popover>
                      </Select>
                    </div>
                  );
                }}
              />
              {errors.activityTypePath ? (
                <p className="text-danger text-sm">{errors.activityTypePath.message as string}</p>
              ) : null}
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                活动标题 <span className="text-danger">*</span>
              </Label>
              <Controller
                control={form.control}
                name="title"
                render={({ field }) => (
                  <TextField isInvalid={!!errors.title}>
                    <Input
                      placeholder="请输入活动标题"
                      maxLength={200}
                      isDisabled={isSubmitting}
                      value={field.value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        field.onChange(e.target.value)
                      }
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </TextField>
                )}
              />
              {errors.title ? <p className="text-danger text-sm">{errors.title.message}</p> : null}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium">
                  活动形式 <span className="text-danger">*</span>
                </Label>
                <Controller
                  control={form.control}
                  name="mode"
                  render={({ field }) => (
                    <Select
                      isDisabled={isSubmitting}
                      value={field.value}
                      onChange={(key) => {
                        if (key != null) field.onChange(String(key));
                      }}
                    >
                      <Select.Trigger className="w-full">
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox>
                          <ListBox.Item id="offline" textValue="线下">
                            线下
                            <ListBox.ItemIndicator />
                          </ListBox.Item>
                          <ListBox.Item id="online" textValue="线上">
                            线上
                            <ListBox.ItemIndicator />
                          </ListBox.Item>
                        </ListBox>
                      </Select.Popover>
                    </Select>
                  )}
                />
                {errors.mode ? <p className="text-danger text-sm">{errors.mode.message}</p> : null}
              </div>
              <div className="flex flex-col gap-2">
                {modeValue === 'offline' ? (
                  <>
                    <Label className="text-sm font-medium">活动地点</Label>
                    <Controller
                      control={form.control}
                      name="location"
                      render={() => (
                        <div className="flex items-start gap-2">
                          <TextField className="min-w-0 flex-1">
                            <Input
                              placeholder="请输入活动具体地址"
                              value={locationInputText}
                              maxLength={500}
                              isDisabled={isSubmitting}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const text = e.target.value || '';
                                setLocationInputText(text);
                                form.setValue('location', text.trim() ? { address: text.trim() } : null, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                              }}
                            />
                          </TextField>
                          <Button
                            type="button"
                            variant="secondary"
                            isDisabled={isSubmitting}
                            onPress={() => setAmapLocationDialogOpen(true)}
                          >
                            <IconMapPin className="mr-1 size-4" />
                            选择地点
                          </Button>
                        </div>
                      )}
                    />
                  </>
                ) : (
                  <>
                    <Label className="text-sm font-medium">活动链接</Label>
                    <Controller
                      control={form.control}
                      name="onlineLink"
                      render={({ field }) => (
                        <TextField>
                          <Input
                            placeholder="请输入链接地址"
                            value={field.value || ''}
                            maxLength={500}
                            isDisabled={isSubmitting}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              field.onChange(e.target.value || null)
                            }
                          />
                        </TextField>
                      )}
                    />
                  </>
                )}
              </div>
            </div>

            <LocationPickerDialog
              open={amapLocationDialogOpen}
              onOpenChange={setAmapLocationDialogOpen}
              disabled={isSubmitting}
              initialAddress={locationInputText}
              initialPayload={form.watch('location') as Record<string, unknown> | null}
              onPick={(payload, text) => {
                setLocationInputText(text || '');
                form.setValue('location', payload || null, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
            />

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium">
                  活动开始时间 <span className="text-danger">*</span>
                </Label>
                <Controller
                  control={form.control}
                  name="startTime"
                  render={({ field }) => (
                    <DateTimePickerField
                      granularity="minute"
                      hourCycle="24-hour"
                      fullWidth
                      className="w-full"
                      isDisabled={isSubmitting}
                      value={isoStringToDateValue(
                        typeof field.value === 'string' ? field.value : undefined
                      )}
                      onChange={(v) => field.onChange(v ? dateValueToIsoString(v) : '')}
                    />
                  )}
                />
                {errors.startTime ? <p className="text-danger text-sm">{errors.startTime.message}</p> : null}
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium">
                  活动结束时间 <span className="text-danger">*</span>
                </Label>
                <Controller
                  control={form.control}
                  name="endTime"
                  render={({ field }) => (
                    <DateTimePickerField
                      granularity="minute"
                      hourCycle="24-hour"
                      fullWidth
                      className="w-full"
                      isDisabled={isSubmitting}
                      value={isoStringToDateValue(
                        typeof field.value === 'string' ? field.value : undefined
                      )}
                      onChange={(v) => field.onChange(v ? dateValueToIsoString(v) : '')}
                    />
                  )}
                />
                {errors.endTime ? <p className="text-danger text-sm">{errors.endTime.message}</p> : null}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">封面图</Label>
              {(() => {
                const coverImage = form.watch('coverImage');
                const previewUrl =
                  uploadFileList[0]?.url ?? uploadFileList[0]?.thumbUrl ?? coverImage ?? undefined;
                return (
                  <div className="flex flex-wrap items-start gap-3">
                    <input
                      ref={coverFileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onCoverFileSelected}
                    />
                    {previewUrl ? (
                      <div className="relative size-28 overflow-hidden rounded-lg border border-divider">
                        <button
                          type="button"
                          className="block size-full"
                          onClick={() => window.open(previewUrl, '_blank')}
                        >
                          <img
                            src={previewUrl}
                            alt=""
                            className="size-full object-cover"
                          />
                        </button>
                        <Button
                          size="sm"
                          variant="danger"
                          className="absolute right-1 top-1"
                          isDisabled={isSubmitting || coverUploadBusy}
                          onPress={() => void removeCoverImage()}
                        >
                          移除
                        </Button>
                      </div>
                    ) : null}
                    <Button
                      variant="secondary"
                      isDisabled={isSubmitting || coverUploadBusy}
                      onPress={() => coverFileInputRef.current?.click()}
                    >
                      {coverUploadBusy ? <Spinner className="mr-2 size-4" /> : null}
                      上传封面
                    </Button>
                  </div>
                );
              })()}
              {errors.coverImage ? (
                <p className="text-danger text-sm">{errors.coverImage.message as string}</p>
              ) : null}
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">活动介绍</Label>
              <Controller
                control={form.control}
                name="description"
                render={({ field }) => (
                  <TextField className="w-full">
                    <TextArea
                      placeholder="请输入活动介绍"
                      value={field.value || ''}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        field.onChange(e.target.value || null)
                      }
                      isDisabled={isSubmitting}
                      className="min-h-[30rem]"
                    />
                  </TextField>
                )}
              />
            </div>

            <Fieldset className="m-0 flex min-w-0 flex-col gap-4 border-0 p-0">
              <Fieldset.Legend className="text-base font-bold">海报设置</Fieldset.Legend>

              {posterFields.map((_field, index) => {
                const fileList = posterUploadLists.get(index) || [];
                const posterUrl =
                  fileList[0]?.url ?? fileList[0]?.thumbUrl ?? watchedPosters[index]?.url;
                const uploading = fileList[0]?.status === 'uploading';

                return (
                  <div key={`poster-${index}-${_field.id}`} className="space-y-4 rounded-lg border border-divider p-4">
                    <div className="flex gap-4">
                      <div className="w-[120px] shrink-0 overflow-hidden">
                        <Label className="mb-1 block text-sm font-medium">海报图片</Label>
                        <div className="flex flex-col gap-2">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            id={`poster-file-${index}`}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              void handlePosterFileInput(index, e)
                            }
                          />
                          {posterUrl ? (
                            <div className="relative aspect-square w-full overflow-hidden rounded-md border border-divider">
                              <a href={posterUrl} target="_blank" rel="noreferrer" className="block size-full">
                                <img src={posterUrl} alt="" className="size-full object-cover" />
                              </a>
                              <Button
                                size="sm"
                                variant="danger"
                                className="absolute right-1 top-1"
                                isDisabled={isSubmitting || uploading}
                                onPress={() => void removePosterImageAt(index)}
                              >
                                移除
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="secondary"
                              className="w-full"
                              isDisabled={isSubmitting || uploading}
                              onPress={() => document.getElementById(`poster-file-${index}`)?.click()}
                            >
                              {uploading ? <Spinner className="mr-1 size-4" /> : null}
                              上传
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="min-w-0 flex-1 space-y-4">
                        <TextField className="w-full">
                          <Label className="text-sm font-medium">名称</Label>
                          <Controller
                            control={form.control}
                            name={`posters.${index}.name`}
                            render={({ field }) => (
                              <Input
                                className="w-full"
                                placeholder="请输入海报名称"
                                value={field.value || ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                  field.onChange(e.target.value || null)
                                }
                                isDisabled={isSubmitting}
                                maxLength={200}
                              />
                            )}
                          />
                        </TextField>
                        <TextField className="w-full">
                          <Label className="text-sm font-medium">描述</Label>
                          <Controller
                            control={form.control}
                            name={`posters.${index}.description`}
                            render={({ field }) => (
                              <TextArea
                                placeholder="请输入海报说明"
                                value={field.value || ''}
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                  field.onChange(e.target.value || null)
                                }
                                isDisabled={isSubmitting}
                                maxLength={1000}
                                className="min-h-24 resize-none"
                              />
                            )}
                          />
                        </TextField>
                      </div>
                    </div>
                  </div>
                );
              })}

              {hasPosterImages ? (
                <div className="flex w-full flex-row flex-wrap gap-2">
                  <Label className="sr-only">海报条目操作</Label>
                  <Button
                    type="button"
                    variant="secondary"
                    isDisabled={isSubmitting}
                    onPress={handleRemoveLastPoster}
                  >
                    删除上一张
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    isDisabled={isSubmitting}
                    onPress={handleAddPosterRow}
                  >
                    再添加一张
                  </Button>
                </div>
              ) : null}
            </Fieldset>

            <Fieldset className="m-0 mt-4 flex min-w-0 flex-col gap-4 border-0 p-0">
              <Fieldset.Legend className="text-base font-bold">报名设置</Fieldset.Legend>

              <div className="w-full">
                <TextField className="w-full">
                  <Label className="text-sm font-medium">报名截止时间</Label>
                  <Controller
                    control={form.control}
                    name="signupEndTime"
                    render={({ field }) => (
                      <DateTimePickerField
                        granularity="minute"
                        hourCycle="24-hour"
                        fullWidth
                        className="w-full"
                        isDisabled={isSubmitting}
                        value={isoStringToDateValue(
                          typeof field.value === 'string' ? field.value : undefined
                        )}
                        onChange={(v) =>
                          field.onChange(v ? dateValueToIsoString(v) : null)
                        }
                      />
                    )}
                  />
                </TextField>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <TextField className="w-full">
                  <Label className="text-sm font-medium">限制人数</Label>
                  <Controller
                    control={form.control}
                    name="quota"
                    render={({ field }) => (
                      <Input
                        type="number"
                        placeholder="不限制则留空"
                        value={field.value || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          field.onChange(e.target.value || null)
                        }
                        isDisabled={isSubmitting}
                        min={0}
                        className="w-full"
                      />
                    )}
                  />
                </TextField>
                <div className="w-full">
                  <Label className="mb-1 block text-sm font-medium">报名后是否允许取消</Label>
                  <div className="border-divider flex h-9 w-full flex-row items-center justify-between rounded-lg border px-3 sm:h-8">
                    <span className="text-muted-foreground text-sm">
                      {allowCancelSignupValue ? '允许' : '不允许'}
                    </span>
                    <Controller
                      control={form.control}
                      name="allowCancelSignup"
                      render={({ field }) => (
                        <Switch
                          isSelected={Boolean(field.value)}
                          onChange={field.onChange}
                          isDisabled={isSubmitting}
                        >
                          <Switch.Content>
                            <Switch.Control>
                              <Switch.Thumb />
                            </Switch.Control>
                          </Switch.Content>
                        </Switch>
                      )}
                    />
                  </div>
                </div>
              </div>
            </Fieldset>

            <Fieldset className="m-0 mt-4 flex min-w-0 flex-col gap-4 border-0 p-0">
              <Fieldset.Legend className="text-base font-bold">统计配置</Fieldset.Legend>

              <div className="flex flex-wrap items-end gap-3">
                <TextField className="min-w-[140px] flex-1">
                  <Label className="text-sm font-medium">周期名称</Label>
                  <Input
                    placeholder="请输入周期名称"
                    value={statAddLabel}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setStatAddLabel(e.target.value)
                    }
                    isDisabled={isSubmitting}
                    className="w-full"
                  />
                </TextField>
                <div className="flex min-w-[160px] flex-1 flex-col gap-1">
                  <Label className="text-sm font-medium">开始时间</Label>
                  <DateTimePickerField
                    granularity="minute"
                    hourCycle="24-hour"
                    fullWidth
                    className="w-full"
                    isDisabled={isSubmitting}
                    value={isoStringToDateValue(statAddStartTime)}
                    onChange={(v) => setStatAddStartTime(v ? dateValueToIsoString(v) : null)}
                  />
                </div>
                <div className="flex min-w-[160px] flex-1 flex-col gap-1">
                  <Label className="text-sm font-medium">结束时间</Label>
                  <DateTimePickerField
                    granularity="minute"
                    hourCycle="24-hour"
                    fullWidth
                    className="w-full"
                    isDisabled={isSubmitting}
                    value={isoStringToDateValue(statAddEndTime)}
                    onChange={(v) => setStatAddEndTime(v ? dateValueToIsoString(v) : null)}
                  />
                </div>
                <div className="shrink-0 self-end">
                  <Label className="sr-only">添加周期</Label>
                  <Button
                    type="button"
                    isDisabled={isSubmitting}
                    onPress={handleAddStatisticalPeriod}
                    className="w-full sm:w-auto"
                  >
                    添加
                  </Button>
                </div>
              </div>

              {statisticalPeriodsValues.length > 0 ? (
                <ul className="w-full space-y-2">
                  {statisticalPeriodsValues.map((p, index) => (
                    <li
                      key={`${p.label}-${p.start_time}-${index}`}
                      className="border-divider grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-3 border-b py-2 last:border-0"
                    >
                      <span className="truncate text-sm">{p.label}</span>
                      <span className="text-muted-foreground text-sm">{formatPeriodDisplay(p.start_time)}</span>
                      <span className="text-muted-foreground text-sm">{formatPeriodDisplay(p.end_time)}</span>
                      <div className="flex min-h-9 items-center justify-end sm:min-h-8">
                        <Button
                          type="button"
                          variant="danger"
                          onPress={() => removePeriodAt(index)}
                          isDisabled={isSubmitting}
                        >
                          删除
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : null}
            </Fieldset>

            <Fieldset className="m-0 mt-4 flex min-w-0 flex-col gap-4 border-0 p-0">
              <Fieldset.Legend className="text-base font-bold">其他配置</Fieldset.Legend>
              <div className="grid grid-cols-2 gap-4">
                <TextField className="w-full">
                  <Label className="text-sm font-medium">谁可见</Label>
                  <Controller
                    control={form.control}
                    name="visibility"
                    render={({ field }) => (
                      <Select
                        isDisabled={isSubmitting}
                        value={field.value}
                        onChange={(key) => {
                          if (key != null) field.onChange(key as VisibilityValue);
                        }}
                      >
                        <Select.Trigger className="w-full">
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                          <ListBox>
                            {(
                              ['SELF', 'ORG', 'ORG_AND_CHILD', 'SPACE', 'ASSIGNEE'] as const
                            ).map((v) => (
                              <ListBox.Item key={v} id={v} textValue={visibilityLabelMap[v]}>
                                {visibilityLabelMap[v]}
                                <ListBox.ItemIndicator />
                              </ListBox.Item>
                            ))}
                          </ListBox>
                        </Select.Popover>
                      </Select>
                    )}
                  />
                </TextField>
                <div className="w-full">
                  <Label className="mb-1 block text-sm font-medium">是否设为模板</Label>
                  <div className="border-divider flex h-9 w-full flex-row items-center justify-between rounded-lg border px-3 sm:h-8">
                    <span className="text-muted-foreground text-sm">
                      {isTemplateValue ? '是' : '否'}
                    </span>
                    <Controller
                      control={form.control}
                      name="isTemplate"
                      render={({ field }) => (
                        <Switch
                          isSelected={Boolean(field.value)}
                          onChange={field.onChange}
                          isDisabled={isSubmitting}
                        >
                          <Switch.Content>
                            <Switch.Control>
                              <Switch.Thumb />
                            </Switch.Control>
                          </Switch.Content>
                        </Switch>
                      )}
                    />
                  </div>
                </div>
              </div>
            </Fieldset>

          </form>
          )}
          {step === 2 && (
            effectiveActivityId ? (
              <PrizesStepContent activityId={effectiveActivityId} onPrizesChanged={refetchPrizes} />
            ) : null
          )}
          {step === 3 && (
            effectiveActivityId ? (
              <LotteryRulesStepContent
                activityId={effectiveActivityId}
                activityTitle={form.getValues('title') || activityData?.title || ''}
              />
            ) : null
          )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ctxValue: ActivityFormContextModel = {
    onClose,
    isEdit,
    step,
    setStep,
    handleStepChange,
    stepDefs,
    isSubmitting,
    submitIntentRef,
    canEnterStep3,
  };

  return (
    <ActivityFormContext.Provider value={ctxValue}>
      {children({ FormBody: ActivityFormBody })}
    </ActivityFormContext.Provider>
  );
}
